import { Link, useSearchParams } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { Image } from '@mui/icons-material';
import { AppBar, Box, Grid2, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import useAlerts from 'components/Alerts/useAlerts';
import FormSelect from 'components/Form/Select/Select';
import FormSwitch from 'components/Form/Switch/Switch';
import Header from 'components/Header/Header';
import { Papeer } from 'components/Papeer/Papeer';
import { get, isObject } from 'lodash';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store/hooks';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import useValidationSchema from './_form';
import { IFrontendSettingsFormValues } from './_types';
import InputForm from 'components/Form/Input/Input';
import { Save } from '@mui/icons-material';
import { getFrontendConfigFromApi, updateFeConfig } from 'modules/Start/_api';
import { setConfigFromApi } from 'store/reducers/appReducer';
import { useWithTitle } from 'utils/hooks/useWithTitle';
import FrontendSettingsImportUDI from './FrontendSettingsImportUDI';
import { useUser } from 'utils/hooks/useUser';
import GridConfigurations from './GridConfigurations/GridConfigurations';
import Button from 'components/Buttons/Button';
import { useAppInfo } from 'utils/hooks/useAppInfo';

export const FrontendSettings = () => {
  const { t } = useTranslation('Settings');
  useWithTitle(); // sets title to document
  const { toggleLoader } = useAppGlobals();
  const { compactMode } = useAppInfo();
  const dispatch = useAppDispatch();
  const { hasRole } = useUser();

  const { FrontendSettingsFormSchema } = useValidationSchema();
  const { addErrorAlert, addSuccessAlert } = useAlerts();
  const [importImageDialog, setImportImageDialog] = useState<boolean>(false);

  const methods = useForm<IFrontendSettingsFormValues>({
    resolver: yupResolver(FrontendSettingsFormSchema),
    defaultValues: {
      primaryColor: '',
      login: {
        background: '',
        logo: {
          application: {
            variant: 'light',
          },
          marie: {
            src: '',
            height: 0,
          },
          customer: {
            src: '',
            height: 0,
          },
        },
        leftBlock: {
          color: '',
        },
        footer: {
          color: '',
          hoverColor: '',
        },
      },
      udi: '',
    },
    mode: 'all',
  });

  const selectedValues = methods.watch();

  const getFeConfigFromApiAndStore = async () => {
    const config = await getFrontendConfigFromApi();
    dispatch(setConfigFromApi(get(config, 'config')));
  };

  const submitHandler = methods.handleSubmit(async (values) => {
    try {
      toggleLoader();
      await updateFeConfig(values);
      await getFeConfigFromApiAndStore();
      addSuccessAlert(t('frontendSettingsSaved'));
    } catch (e) {
      addErrorAlert(t('frontendSettingsNotSaved'));
    } finally {
      toggleLoader(false);
    }
  });

  const fetchFeConfigForEdit = async () => {
    toggleLoader();
    try {
      const feSettings = await getFrontendConfigFromApi();
      if (isObject(feSettings)) {
        methods.reset(feSettings.config);
      }
    } catch (e) {
      console.error('errorEditing');
      console.error(e);
    } finally {
      toggleLoader(false);
    }
  };

  useEffect(() => {
    fetchFeConfigForEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || '';
  const [selectedTab, setSelectedTab] = useState(tabFromUrl || 'app');

  const toggleImportImageDialog = (state: boolean = true) => {
    setImportImageDialog(state);
  };

  return (
    <>
      <Header title={t('frontendSettings')} />
      <AppBar position="static" color="default" sx={{ mb: compactMode ? 1 : 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(_e, value) => setSelectedTab(value)}
          aria-label="Frontend tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label={t('application')} value="app" data-pw="tab-application" />
          <Tab label={t('loginScreen')} value="login" data-pw="tab-login-screen" />
          {(hasRole('ROLE_STUDY_GRID_MANAGER') ||
            hasRole('ROLE_PATIENT_DOCUMENTATION_GRID_MANAGER')) && (
            <Tab
              label={t('defaultGridConfiguration')}
              value="gridConfiguration"
              data-pw="tab-default-grid-configuration"
            />
          )}
        </Tabs>
      </AppBar>

      <FormProvider {...methods}>
        <form onSubmit={submitHandler}>
          {/* App tab panel */}
          <Box sx={{ py: 0, display: selectedTab === 'app' ? 'block' : 'none' }}>
            <Header title={t('colors')} />
            <Papeer bottomMargin={true}>
              <Grid2 container={true} spacing={1}>
                <Grid2 size={{ xs: 12, sm: 6, lg: 3, xl: 3 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        border: '2px solid #000',
                        background: selectedValues.primaryColor,
                        position: 'absolute',
                        right: (theme) => theme.spacing(4),
                        top: (theme) => (compactMode ? theme.spacing(1.4) : theme.spacing(1.8)),
                      }}
                    />
                  </Box>
                  <InputForm name="primaryColor" label={t('primaryColor')} placeholder="#XXXXXX" />
                </Grid2>
                <Grid2 size={12} />
                <Grid2 size={{ xs: 12, sm: 6, lg: 3, xl: 3 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        border: '2px solid #000',
                        background: selectedValues.menuBackground,
                        position: 'absolute',
                        right: (theme) => theme.spacing(4),
                        top: (theme) => (compactMode ? theme.spacing(1.4) : theme.spacing(1.8)),
                      }}
                    />
                  </Box>
                  <InputForm
                    name="menuBackground"
                    label={t('menuBackground')}
                    placeholder="#XXXXXX"
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, lg: 3, xl: 3 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        border: '2px solid #000',
                        background: selectedValues.menuColor,
                        position: 'absolute',
                        right: (theme) => theme.spacing(4),
                        top: (theme) => (compactMode ? theme.spacing(1.4) : theme.spacing(1.8)),
                      }}
                    />
                  </Box>
                  <InputForm name="menuColor" label={t('menuColor')} placeholder="#XXXXXX" />
                </Grid2>
              </Grid2>
            </Papeer>
            <Header title={t('logos')} />
            <Papeer bottomMargin={true}>
              <Grid2 container={true} spacing={1}>
                <Grid2 size={12}>
                  <Typography variant="h6">{t('menuLogo')}</Typography>
                </Grid2>
                <Grid2 size={12}>
                  <InputForm name={`menuLogo.src`} label={t('base64')} />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
                  <InputForm
                    name={`menuLogo.width`}
                    label={t('width')}
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
                  <InputForm
                    name={`menuLogo.height`}
                    label={t('height')}
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid2>
                {get(selectedValues, `menuLogo.src`) ? (
                  <Grid2 size={12}>
                    <Box
                      sx={{
                        background: selectedValues.menuBackground,
                        p: 2,
                        display: 'inline-flex',
                      }}
                    >
                      <img
                        src={get(selectedValues, `menuLogo.src`, '')}
                        width={get(selectedValues, `menuLogo.width`, 0)}
                        height={get(selectedValues, `menuLogo.height`, 0)}
                        alt="Logo"
                      />
                    </Box>
                  </Grid2>
                ) : null}
              </Grid2>
            </Papeer>
            <Header title={t('texts')} />
            <Papeer>
              <Grid2 container={true} spacing={1}>
                <Grid2 size={{ xs: 12, sm: 6, lg: 3, xl: 3 }}>
                  <InputForm name="mainTitle" label={t('mainTitle')} />
                </Grid2>
              </Grid2>
            </Papeer>
            <Header title="UDI" />
            <Papeer>
              <Grid2 container={true} spacing={1} alignItems="center">
                <Grid2 size={{ xs: 12, sm: 8, lg: 6, xl: 4 }}>
                  <InputForm name="udi" label={t('udiValue')} />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, lg: 3, xl: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    className="submit-form-button"
                    onClick={() => {
                      toggleImportImageDialog(true);
                    }}
                  >
                    {t('uploadUDI')}
                  </Button>
                </Grid2>
              </Grid2>
            </Papeer>
          </Box>

          {/* Login tab panel */}
          <Box sx={{ py: 0, display: selectedTab === 'login' ? 'block' : 'none' }}>
            <Header title={t('loginScreen')} />
            <Papeer>
              <Grid2 container={true} spacing={1}>
                <Grid2 size={12}>
                  <InputForm
                    name="login.leftBlock.text"
                    label={t('text')}
                    rows={3}
                    multiline={true}
                    fullWidth={true}
                  />
                </Grid2>{' '}
                <Grid2 size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
                  <Box sx={{ position: 'relative' }}>
                    <InputForm
                      name="login.background"
                      label={t('loginPageBackgroundColor')}
                      placeholder="#XXXXXX"
                    />
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        border: '2px solid #000',
                        background: selectedValues.login.background,
                        position: 'absolute',
                        right: (theme) => theme.spacing(4),
                        top: (theme) => (compactMode ? theme.spacing(1.4) : theme.spacing(1.8)),
                      }}
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
                  <Box sx={{ position: 'relative' }}>
                    <InputForm
                      name="login.leftBlock.color"
                      label={t('loginPageColor')}
                      placeholder="#XXXXXX"
                    />
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        border: '2px solid #000',
                        background: selectedValues.login.leftBlock.color,
                        position: 'absolute',
                        right: (theme) => theme.spacing(4),
                        top: (theme) => (compactMode ? theme.spacing(1.4) : theme.spacing(1.8)),
                      }}
                    />
                  </Box>
                </Grid2>
                <Grid2 size={12}>
                  <Box sx={{ background: selectedValues.login.background, p: 3 }}>
                    <Typography
                      component="p"
                      sx={{
                        color: selectedValues.login.leftBlock.color,
                        fontSize: {
                          xs: 20,
                          lg: 26,
                        },
                        fontWeight: 300,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {selectedValues.login.leftBlock.text}
                    </Typography>
                    <Typography
                      component="ul"
                      sx={{
                        listStyle: 'none',
                        p: 0,
                        m: 0,
                        mt: 2,
                        mb: {
                          xs: 2,
                          lg: 0,
                        },
                        color: selectedValues.login.footer.color,
                        '& a': {
                          color: 'inherit',
                          textDecoration: 'none',
                        },
                        '& > li > a:hover, & > li > p:hover': {
                          color: `${selectedValues.login.footer.hoverColor} !important`,
                        },
                        fontSize: 16,
                        fontWeight: 300,
                      }}
                    >
                      <Box
                        component="li"
                        sx={{
                          position: 'relative',
                          mr: { xs: 0, lg: 3 },
                          display: { lg: 'inline-block' },
                        }}
                      >
                        <a href="https://orcz.cz" target="_blank" rel="noreferrer">
                          {t('footerLink')}
                        </a>
                      </Box>
                    </Typography>
                  </Box>
                </Grid2>
              </Grid2>
            </Papeer>

            <Header title={t('footer.title')} />
            <Papeer>
              <Grid2 container={true} spacing={1}>
                <Grid2 size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
                  <Box sx={{ position: 'relative' }}>
                    <InputForm
                      name="login.footer.color"
                      label={t('footerColor')}
                      placeholder="#XXXXXX"
                    />
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        border: '2px solid #000',
                        background: selectedValues.login.footer.color,
                        position: 'absolute',
                        right: (theme) => theme.spacing(4),
                        top: (theme) => (compactMode ? theme.spacing(1.4) : theme.spacing(1.8)),
                      }}
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
                  <Box sx={{ position: 'relative' }}>
                    <InputForm
                      name="login.footer.hoverColor"
                      label={t('footerHoverColor')}
                      placeholder="#XXXXXX"
                    />
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        border: '2px solid #000',
                        background: selectedValues.login.footer.hoverColor,
                        position: 'absolute',
                        right: (theme) => theme.spacing(4),
                        top: (theme) => (compactMode ? theme.spacing(1.4) : theme.spacing(1.8)),
                      }}
                    />
                  </Box>
                </Grid2>

                <Grid2 size={12}>
                  <Header title={t('footer.links')} />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, lg: 4, xl: 4 }}>
                  <Header title={t('footer.orCzLink')} fontSize={14} />
                  <FormSwitch name="login.footer.orCzLink.show" label={t('footer.show')} />
                  <InputForm name="login.footer.orCzLink.url" label={t('footer.link')} />
                  <InputForm name="login.footer.orCzLink.text" label={t('footer.text')} />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, lg: 4, xl: 4 }}>
                  <Header title={t('footer.helpdesk')} fontSize={14} />
                  <FormSwitch name="login.footer.helpdesk.show" label={t('footer.show')} />
                  <InputForm name="login.footer.helpdesk.url" label={t('footer.link')} />
                  <InputForm name="login.footer.helpdesk.text" label={t('footer.text')} />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, lg: 4, xl: 4 }}>
                  <Header title={t('footer.label')} fontSize={14} />
                  <FormSwitch name="login.footer.label.show" label={t('footer.show')} />
                  <FormSwitch
                    name="login.footer.label.visibleOnStartup"
                    label={t('footer.visibleOnStartup')}
                  />
                  <InputForm name="login.footer.label.text" label={t('footer.text')} />
                  <InputForm name="login.footer.label.src" label={t('base64')} />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, lg: 6, xl: 4 }}>
                  <Header title={t('footer.contact')} fontSize={14} />
                  <FormSwitch name="login.footer.contact.show" label={t('footer.show')} />
                  <InputForm name="login.footer.contact.text" label={t('footer.text')} />
                  {['day', 'night', 'weekend'].map((dayTime) => (
                    <>
                      <Typography sx={{ fontWeight: 'bold' }}>{t(`footer.${dayTime}`)}</Typography>
                      <Box
                        key={dayTime}
                        sx={{
                          display: 'flex',
                          flexDirection: {
                            xs: 'column',
                            md: 'row',
                          },
                        }}
                      >
                        <FormSwitch
                          name={`login.footer.contact.hours.${dayTime}.show`}
                          label={t('footer.show')}
                        />
                        {dayTime !== 'weekend' && (
                          <InputForm
                            name={`login.footer.contact.hours.${dayTime}.hours`}
                            label={t(`footer.${dayTime}`)}
                            sx={{ mr: 1 }}
                          />
                        )}

                        <InputForm
                          name={`login.footer.contact.hours.${dayTime}.phone`}
                          label={t('footer.phone')}
                        />
                      </Box>
                    </>
                  ))}
                </Grid2>

                <Grid2 size={12}>
                  <Header title={t('footer.help')} />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 12, lg: 8, xl: 6 }}>
                  <FormSwitch name="login.helpBlock.show" label={t('footer.show')} />
                  <InputForm name="login.helpBlock.text" label={t('footer.help')} />
                </Grid2>
              </Grid2>
            </Papeer>

            <Box sx={{ py: 0, display: 'flex', alignItems: 'center' }}>
              <Header title={t('logos')} />
              <Tooltip title={t('openExternalConverter')}>
                <IconButton
                  sx={{ ml: 1 }}
                  onClick={() => window.open('https://www.base64-image.de/', '_blank')}
                >
                  <Image />
                </IconButton>
              </Tooltip>
            </Box>
            <Papeer>
              <Grid2 container={true} spacing={1}>
                <Grid2 size={12}>
                  <Header title={t('logo.application')} />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <FormSelect
                    name="login.logo.application.variant"
                    label={t('logo.logoVariant')}
                    items={[
                      {
                        id: 'portal-white',
                        label: t('logo.variant.lightPortal'),
                      },
                      { id: 'portal-black', label: t('logo.variant.darkPortal') },
                      {
                        id: 'klinicky-portal-white',
                        label: t('logo.variant.lightClinicalPortal'),
                      },
                      {
                        id: 'klinicky-portal-black',
                        label: t('logo.variant.darkClinicalPortal'),
                      },
                    ]}
                  />
                  <Box
                    sx={{
                      background: selectedValues.login.background,
                      p: 2,
                    }}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/img/${selectedValues.login.logo.application.variant}.svg`}
                      alt="Logo"
                    />
                  </Box>
                </Grid2>
              </Grid2>
              {['marie', 'customer'].map((logo) => (
                <Grid2 container={true} spacing={1}>
                  <Grid2 size={12} sx={{ mt: 2 }}>
                    <Header title={t(`logo.${logo}`)} />
                  </Grid2>
                  <Grid2 size={12}>
                    <InputForm name={`login.logo.${logo}.src`} label={t('base64')} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
                    <InputForm
                      name={`login.logo.${logo}.height`}
                      label={t('height')}
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid2>
                  {get(selectedValues, `login.logo.${logo}.src`) ? (
                    <Grid2 size={12}>
                      <Box
                        sx={{
                          background: selectedValues.login.background,
                          p: 2,
                          display: 'inline-flex',
                        }}
                      >
                        <img
                          src={get(selectedValues, `login.logo.${logo}.src`, '')}
                          height={get(selectedValues, `login.logo.${logo}.height`, 0)}
                          alt="Logo"
                        />
                      </Box>
                    </Grid2>
                  ) : null}
                </Grid2>
              ))}
            </Papeer>
          </Box>

          {/* Default grid appearance tab panel */}
          {selectedTab === 'gridConfiguration' && (
            <Box sx={{ py: 0, display: selectedTab === 'gridConfiguration' ? 'block' : 'none' }}>
              <GridConfigurations />
            </Box>
          )}

          {selectedTab !== 'gridConfiguration' ? (
            <Box sx={{ my: 1, justifyContent: 'left', display: 'flex' }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                className="submit-form-button"
              >
                {t('Form:save')}
              </Button>
              <Button
                variant="contained"
                color="inherit"
                component={Link}
                {...({ to: '/' } as any)}
                sx={{ ml: 1 }}
                data-tour="backButton"
              >
                {t('Header:back')}
              </Button>
            </Box>
          ) : null}
        </form>
      </FormProvider>
      {importImageDialog && (
        <FrontendSettingsImportUDI
          toggleImportImageDialog={toggleImportImageDialog}
          setValue={methods.setValue}
        />
      )}
    </>
  );
};
